import React, { lazy, Suspense, useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, useParams } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { AppShell } from '@/pipeline/components/AppShell';
import { computeIsLocal, buildRbacRedirectUrl } from './auth';

const LandingPage = lazy(() => import('@/pipeline/pages/LandingPage').then(m => ({ default: m.LandingPage })));
const AnalysisSelectorPage = lazy(() => import('@/pipeline/pages/AnalysisSelectorPage').then(m => ({ default: m.AnalysisSelectorPage })));
const AnalysisRunPage = lazy(() => import('@/pipeline/pages/AnalysisRunPage').then(m => ({ default: m.AnalysisRunPage })));
const PresetsPage = lazy(() => import('@/pipeline/pages/PresetsPage').then(m => ({ default: m.PresetsPage })));
const DashboardPage = lazy(() => import('@/pipeline/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const PipelineDemoPage = lazy(() => import('@/pages/PipelineDemoPage').then(m => ({ default: m.PipelineDemoPage })));
const TemplateTesterPage = lazy(() => import('@/pipeline/pages/TemplateTesterPage').then(m => ({ default: m.TemplateTesterPage })));
const ReportPage = lazy(() => import('@/pipeline/pages/ReportPage').then(m => ({ default: m.ReportPage })));
const DataSourcesPage = lazy(() => import('@/pipeline/pages/DataSourcesPage').then(m => ({ default: m.DataSourcesPage })));
const LocalAnalysisPage = lazy(() => import('@/pipeline/pages/LocalAnalysisPage').then(m => ({ default: m.LocalAnalysisPage })));
const DatabricksRunPage = lazy(() => import('@/pipeline/pages/DatabricksRunPage').then(m => ({ default: m.DatabricksRunPage })));
const AllAnalysesPage = lazy(() => import('@/pipeline/pages/AllAnalysesPage').then(m => ({ default: m.AllAnalysesPage })));

function PageLoader() {
  return (
    <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress />
    </Box>
  );
}

const RBAC_UI_URL = (import.meta.env.VITE_QS_RBAC_UI_URL as string) ?? '';
const BE_URL = (import.meta.env.VITE_QS_BE_URL as string) ?? '';
const FORCE_RBAC = import.meta.env.VITE_FORCE_RBAC === 'true';
// Explicit dev-auth opt-in, baked into the dev image build (dev-up.sh passes
// --build-arg VITE_DEV_AUTH=true). This is what marks a build as "local" when
// it is not served from localhost — e.g. a Codespace on *.app.github.dev.
// It is never set in production builds, so prod always takes the RBAC path.
const DEV_AUTH = import.meta.env.VITE_DEV_AUTH === 'true';
const IS_LOCAL = computeIsLocal({
  forceRbac: FORCE_RBAC,
  devAuth: DEV_AUTH,
  hostname: globalThis.location.hostname,
});

// Redirect to the RBAC login UI, preserving where the user was headed. Refuses
// to redirect when RBAC_UI_URL is unconfigured (buildRbacRedirectUrl returns
// null) — otherwise an empty base loops with an ever-growing ?returnTo until
// the URL exceeds the server limit (HTTP 414).
function redirectToRbac(): void {
  const url = buildRbacRedirectUrl(RBAC_UI_URL, globalThis.location.href);
  if (!url) {
    console.error('RBAC required but VITE_QS_RBAC_UI_URL is not configured; refusing to redirect.');
    return;
  }
  globalThis.location.href = url;
}

function getRbacUserCookie(): string | null {
  for (const chunk of document.cookie.split(';')) {
    const [rawName, ...rest] = chunk.trim().split('=');
    if (rawName.trim() === 'user') return rest.join('=');
  }
  return null;
}

function getCookieValue(name: string): string | null {
  for (const chunk of document.cookie.split(';')) {
    const [rawName, ...rest] = chunk.trim().split('=');
    if (rawName.trim() === name) return rest.join('=');
  }
  return null;
}

function RequireAuth({ children }: Readonly<{ children: React.ReactElement }>) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const accessToken = getCookieValue('access_token');
    if (accessToken) {
      // access_token cookie is set by Common Services RBAC on login.
      // No need to exchange it — APIs forward it directly as Bearer token.
      setReady(true);
    } else if (IS_LOCAL) {
      // Local dev without RBAC login: allow through so developers can test
      // against a locally-running backend without a full SSO flow.
      setReady(true);
    } else {
      redirectToRbac();
    }
  }, []);

  if (!ready) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return children;
}

function DataSourcesGroomRedirect() {
  const { datasourceId } = useParams<{ datasourceId: string }>();
  return <Navigate to={`/pipeline/data-sources?groomId=${datasourceId ?? ''}`} replace />;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/pipeline" replace />,
  },
  {
    path: '/pipeline',
    element: <RequireAuth><AppShell /></RequireAuth>,
    children: [
      { index: true, element: <Suspense fallback={<PageLoader />}><LandingPage /></Suspense> },
      { path: 'new', element: <Suspense fallback={<PageLoader />}><AnalysisSelectorPage /></Suspense> },
      { path: 'run/:runId', element: <Suspense fallback={<PageLoader />}><AnalysisRunPage /></Suspense> },
      { path: 'run/:runId/report', element: <Suspense fallback={<PageLoader />}><ReportPage /></Suspense> },
      { path: 'run/:runId/dashboard', element: <Suspense fallback={<PageLoader />}><DashboardPage /></Suspense> },
      { path: 'data-sources', element: <Suspense fallback={<PageLoader />}><DataSourcesPage /></Suspense> },
      { path: 'presets', element: <Suspense fallback={<PageLoader />}><PresetsPage /></Suspense> },
      { path: 'demo', element: <Suspense fallback={<PageLoader />}><PipelineDemoPage /></Suspense> },
      { path: 'template-tester', element: <Suspense fallback={<PageLoader />}><TemplateTesterPage /></Suspense> },
      { path: 'local', element: <Suspense fallback={<PageLoader />}><LocalAnalysisPage /></Suspense> },
      { path: 'databricks-run/:runId', element: <Suspense fallback={<PageLoader />}><DatabricksRunPage /></Suspense> },
      { path: 'analyses', element: <Suspense fallback={<PageLoader />}><AllAnalysesPage /></Suspense> },
    ],
  },
  {
    path: '/datasources/:datasourceId/groom/:sessionId',
    element: <RequireAuth><DataSourcesGroomRedirect /></RequireAuth>,
  },
  {
    path: '/datasources/:datasourceId/groom',
    element: <RequireAuth><DataSourcesGroomRedirect /></RequireAuth>,
  },
], { basename: (import.meta.env.VITE_APP_BASENAME as string) || '/' });

export const AppRouter: React.FC = () => <RouterProvider router={router} />;
